import type {
  NetworkDashboardAvailableStore,
  NetworkDashboardStoreAgeFilter,
} from '@gozoki/api-types'
import { DateTime } from 'luxon'
import { storeAgeFromFilter } from './store-age'
import { DASHBOARD_ALL_TIME_START_ISO, type NetworkDashboardDateRange } from './types'

export interface AllTimeStoreScope {
  storeIds: number[]
  storeAgeFilter: NetworkDashboardStoreAgeFilter
  ageStoreIds?: number[]
}

// Mêmes règles que `resolveKpiStoreFilter` côté backend : les `ageStoreIds` explicites priment
// sur le preset d'ancienneté, et l'un comme l'autre s'intersectent avec le filtre géographique.
function storesInScope(
  availableStores: NetworkDashboardAvailableStore[],
  scope: AllTimeStoreScope,
  periodEndIso: string,
): NetworkDashboardAvailableStore[] {
  const geographic =
    scope.storeIds.length > 0
      ? availableStores.filter((store) => scope.storeIds.includes(store.id))
      : availableStores

  if (scope.ageStoreIds !== undefined) {
    const allowed = new Set(scope.ageStoreIds)
    return geographic.filter((store) => allowed.has(store.id))
  }

  const ageState = storeAgeFromFilter(
    scope.storeAgeFilter,
    undefined,
    availableStores,
    periodEndIso,
  )
  if (ageState.mode === 'default') {
    return geographic
  }
  return geographic.filter((store) => ageState.selectedStoreIds.has(store.id))
}

/**
 * Date à laquelle « Toujours » démarre réellement pour un périmètre : la première vente de la
 * boutique la plus ancienne du lot. `ageReferenceDate` est la référence d'ancienneté servie par
 * le backend (première vente, avec repli sur la date d'ouverture pour une boutique qui n'a
 * jamais vendu). Repli sur la sentinelle tant que la liste des boutiques n'est pas chargée.
 */
export function resolveAllTimeStartIso(
  availableStores: NetworkDashboardAvailableStore[],
  scope: AllTimeStoreScope,
  periodEndIso: string,
): string {
  const references = storesInScope(availableStores, scope, periodEndIso)
    .map((store) => store.ageReferenceDate)
    .sort()
  return references[0] ?? DASHBOARD_ALL_TIME_START_ISO
}

/**
 * Date de début à envoyer à l'API pour une sélection. `from: null` est le marqueur « Toujours »
 * de l'UI : il ne doit jamais partir tel quel vers l'API, sinon la période démarre au début de
 * Lucie même pour une boutique ouverte l'an dernier.
 */
export function selectionStartIso(
  selection: AllTimeStoreScope & { dateRange: NetworkDashboardDateRange },
  availableStores: NetworkDashboardAvailableStore[],
): string {
  if (selection.dateRange.from) {
    return DateTime.fromJSDate(selection.dateRange.from).toISODate()!
  }
  const periodEndIso = DateTime.fromJSDate(selection.dateRange.to).toISODate()!
  return resolveAllTimeStartIso(availableStores, selection, periodEndIso)
}

/**
 * Date de début d'une ligne « Valeur comparable ». En « Toujours » elle hérite du début déjà
 * résolu de la principale au lieu d'être résolue sur son propre périmètre : deux résolutions
 * indépendantes donnent deux fenêtres de longueurs différentes, et tout ce qui est apparié en
 * aval décale (série tronquée complétée par des zéros, graphe vide, comparaison hors sujet).
 */
export function comparableStartIso(
  comparable: { dateRange: NetworkDashboardDateRange },
  principalStartIso: string,
): string {
  return comparable.dateRange.from
    ? DateTime.fromJSDate(comparable.dateRange.from).toISODate()!
    : principalStartIso
}
